import { useState, useCallback, useEffect } from 'react';
import { Edit, Trash2, Shield, UserPlus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../context/AuthContext';
import { userAPI } from '../../lib/api';
import { toast } from 'sonner';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { user: currentUser } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'editor' });

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([userAPI.getAll(), userAPI.getRoles()]);
      setUsers(usersRes.data); setRoles(rolesRes.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      if (err.response?.status === 403) toast.error('আপনার এই পেজে অ্যাক্সেস নেই');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => { setForm({ name: '', email: '', password: '', role: 'editor' }); setEditingUser(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) { await userAPI.update(editingUser.id, { name: form.name, role: form.role }); toast.success('ব্যবহারকারী আপডেট হয়েছে'); }
      else { await userAPI.create(form); toast.success('ব্যবহারকারী তৈরি হয়েছে'); }
      fetchData(); setIsDialogOpen(false); resetForm();
    } catch (err) { toast.error(err.response?.data?.detail || 'কিছু সমস্যা হয়েছে'); }
  };

  const handleEdit = (user) => { setEditingUser(user); setForm({ name: user.name, email: user.email, password: '', role: user.role }); setIsDialogOpen(true); };

  const handleDelete = async (id) => {
    if (window.confirm('এই ব্যবহারকারী মুছে ফেলতে চান?')) {
      try { await userAPI.delete(id); toast.success('ব্যবহারকারী মুছে ফেলা হয়েছে'); fetchData(); }
      catch (err) { toast.error(err.response?.data?.detail || 'মুছতে সমস্যা হয়েছে'); }
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await userAPI.update(user.id, { is_active: !user.is_active });
      toast.success(user.is_active ? 'নিষ্ক্রিয় করা হয়েছে' : 'সক্রিয় করা হয়েছে');
      fetchData();
    } catch (err) { toast.error('আপডেট করতে সমস্যা হয়েছে'); }
  };

  const roleLabels = {
    master_admin: { label: 'মাস্টার অ্যাডমিন', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    admin: { label: 'অ্যাডমিন', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
    moderator: { label: 'মডারেটর', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    editor: { label: 'এডিটর', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' }
  };

  if (currentUser?.role !== 'master_admin') {
    return (
      <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <Shield className="mx-auto text-gray-400 mb-4" size={48} />
        <p className="text-gray-600 dark:text-gray-400">শুধুমাত্র মাস্টার অ্যাডমিন এই পেজে অ্যাক্সেস করতে পারবেন</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">ব্যবহারকারী ম্যানেজমেন্ট</h1>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2E7D32] hover:bg-[#1B5E20] w-full sm:w-auto" data-testid="add-user-btn"><UserPlus size={18} className="mr-2" /> নতুন ব্যবহারকারী</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editingUser ? 'ব্যবহারকারী সম্পাদনা' : 'নতুন ব্যবহারকারী'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block text-sm font-medium mb-1">নাম</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" /></div>
              <div><label className="block text-sm font-medium mb-1">ইমেইল</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={!!editingUser} data-testid="user-email-input" /></div>
              {!editingUser && (<div><label className="block text-sm font-medium mb-1">পাসওয়ার্ড</label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="user-password-input" /></div>)}
              <div>
                <label className="block text-sm font-medium mb-1">রোল</label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="master_admin">মাস্টার অ্যাডমিন</SelectItem>
                    <SelectItem value="admin">অ্যাডমিন</SelectItem>
                    <SelectItem value="moderator">মডারেটর</SelectItem>
                    <SelectItem value="editor">এডিটর</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{form.role && roles[form.role] && (<>Permissions: {roles[form.role].permissions.join(', ')}</>)}</p>
              </div>
              <Button type="submit" className="w-full bg-[#2E7D32] hover:bg-[#1B5E20]" data-testid="user-submit-btn">{editingUser ? 'আপডেট করুন' : 'তৈরি করুন'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-900 dark:text-blue-300 mb-2 flex items-center gap-2"><Shield size={16} /> রোল পারমিশন</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          {Object.entries(roles).map(([role, info]) => (
            <div key={role} className="flex items-center gap-2">
              <Badge className={roleLabels[role]?.color || 'bg-gray-100'}>{roleLabels[role]?.label || role}</Badge>
              <span className="text-gray-600 dark:text-gray-400 text-xs">{info.permissions.join(', ')}</span>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg" />)}</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300">নাম</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300">রোল</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300">স্ট্যাটাস</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300">অ্যাকশন</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{user.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                  </td>
                  <td className="px-4 py-3"><Badge className={`text-xs ${roleLabels[user.role]?.color || 'bg-gray-100'}`}>{roleLabels[user.role]?.label || user.role}</Badge></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={user.is_active} onCheckedChange={() => handleToggleActive(user)} disabled={user.id === currentUser?.id} />
                      <span className={`text-xs ${user.is_active ? 'text-green-600' : 'text-gray-400'}`}>{user.is_active ? 'সক্রিয়' : 'নিষ্ক্রিয়'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(user)} data-testid={`edit-user-${user.id}`}><Edit size={16} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(user.id)} disabled={user.id === currentUser?.id} data-testid={`delete-user-${user.id}`}><Trash2 size={16} className="text-red-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
